import React from 'react';
import {
    betMoney,
    date,
    fixed3,
    flagImage,
    gameImage,
    time,
    isDesktop,
    raceImage
} from 'app/utils';
import MyBetInterface from 'app/interfaces/MyBetInterface';
import {SimpleArtifactImage} from 'app/components/my_profile/artifacts/image';
import I18n from 'app/i18n';
import SmartTooltipReason from 'app/components/reusable/SmartTooltipReason';
import {betToChat, suggestionToChat} from 'app/utils/share_to_chat';
import moment from 'moment';
import {showBetForm, showBetSlip} from 'app/components/table';
import {modalConfirm} from 'app/utils/modals/popups';
import {toastSuccess} from 'app/components/toasts/liteToast';
import {updateInfo} from 'app/common';
import {userBetsStore} from 'app/store/global';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {GAMER_1} from 'app/utils/Consts';
import Post from 'app/utils/Post';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import MySportsbookBetInterface from 'app/interfaces/MySportsbookBetInterface';
import BuyoutButton from 'app/components/my_profile/table-history/BuyoutButton';

const CHAT_AVAILABLE_TIME_SECONDS = 86400;

type Props = (MyBetInterface | MySportsbookBetInterface) & {
  isOpened?: boolean;
  gamer_1?: {nick: string};
  gamer_2?: {nick: string};
  sportsbook?: boolean;
  ticket_id?: string;
  extended_gamer: string;
};

export default class TableHistoryRow extends React.Component<Props> {
    rowStatusBet(): string {
        if (this.props.bought_out) {
            return ' match-bought-out';
        }

        if (this.props.cancelled) {
            return ' match-canceled';
        }

        if (this.props.won !== null) {
            return this.props.won ? ' match-won' : ' match-lost';
        }
        return '';
    }

    cancelSuggestion = (e: React.MouseEvent): void => {
        e && e.preventDefault();
        modalConfirm(I18n.t('cancel_suggestion'), () => {
            new Post({
                params: {ids: [this.props.id]},
                url: '/incubator2/bets/cancel'
            }).execute()
                .then(response => {
                    if (response.ok) {
                        toastSuccess(I18n.t('suggestion_cancelled'));
                        updateInfo();
                        this.setCancel();
                    }
                });
        });
    };

    setCancel(): void {
        userBetsStore.updateCancelledUserBet(this.props.id);
    }

    shareChatButtonAvailable(): boolean {
        if (this.props.sportsbook) {
            return false;
        }
        return this.props.bet.winner !== 0 && moment().unix() - this.props.date > CHAT_AVAILABLE_TIME_SECONDS;
    }

    betMoreAvailable = (): boolean => this.props.bet && !this.props.bet.cancelled;

    renderBuyoutStatus(): React.ReactNode {
        return this.props.bought_out
            ? <span className="history-match-buyout-status">{I18n.t('buyout.status')}</span>
            : null;
    }

    cancelAvailableSuggestion(): boolean {
        return this.props.bet && this.props.suggestion && this.props.started;
    }

    betMore = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e && e.preventDefault();
        const {bet, betMore, on, reloadBets} = this.props;

        if (!betMore) {
            return;
        }

        userBetsStore.setOpenedUserBetId(Number(this.props.id));

        if (this.betMoreAvailable() && !(bet.finished || bet.cancelled)) {
            showBetSlip(bet, on, {selectedBet: bet});
        } else {
            showBetForm(bet, null, {afterBet: reloadBets});
        }
    };

    renderActionsButtons(): React.ReactNode {
        if (!this.props.actionsButtons) {
            return null;
        }

        const res = [];
        const {id, suggestion} = this.props;

        if (this.betMoreAvailable() && this.cancelAvailableSuggestion()) {
            res.push(<button className="button-adaptive button-adaptive_instyle"
                key={`cancel_${id}`} onClick={this.cancelSuggestion}
            >
                <span className="button-name">{I18n.t('user.cancel_label')}</span>
            </button>);
        }

        if (!suggestion && !this.props.sportsbook) {
            res.push(<BuyoutButton bet={this.props as MyBetInterface} key={`buyout_${id}`}/>);
        }
        res.push(this.shareInChatButton());
        return res;
    }

    chatButtonClick = (e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        const {suggestion, id} = this.props;

        if (suggestion) {
            suggestionToChat(id);
        } else {
            betToChat(id);
        }
    };

    shareInChatButton(): React.ReactNode {
        if (this.props.sportsbook) {
            return null;
        }
        return !this.shareChatButtonAvailable() &&
            !this.props.cancelled &&
            <button className="button-adaptive button-adaptive_instyle"
                key={`share_chat_button_${this.props.id}`}
                onClick={this.chatButtonClick}
            >
                <SpriteIcon name="#svg-arrow-link-new" className="vg-default button-icon" />
                <span className="button-name">{I18n.t('chat_title')}</span>
            </button>;
    }

    isNestedBet = (): boolean => this.props?.bet?.game_id > 0;

    onClickCopyMessage = (): void => {
        toastSuccess(I18n.t('single_bet_copied'));
    };

    renderSportsBook = (coef: number, activeClass: string): React.ReactNode => this.props.sportsbook
        ? <span className="history-match-coefficient-sportsbook">{fixed3(coef)}</span>
        : <button
            className={`history-match-coefficient more${activeClass}`}
            onClick={this.props.sportsbook ? null : this.betMore}>
            {fixed3(coef)}
        </button>;

    renderMobile(): React.ReactNode {
        const {
            id, filter, flag, extended_gamer, playmoney, race,
            coef, amount, cancelled, bet_artifacts, win_artifacts, win
        } = this.props;
        // eslint-disable-next-line no-unsafe-optional-chaining
        const {gamer_1, gamer_2} = this.props.bet?.root_line || this.props?.bet || this.props;
        const gamer_nick = this.props.on === GAMER_1 ? gamer_1.nick : gamer_2.nick;
        const activeClass = this.props.isOpened ? ' active' : '';
        const winValue = betMoney({amount: win && win > 0 ? win : amount * coef, playmoney});
        return <div className={`table-content-row-mobile${this.rowStatusBet()}`}>
            <div className="history-match">
                <div className="history-match-date">
                    <span>{date(this.props.date)}</span>
                    <span>{time(this.props.date)}</span>
                    <div className="history-match-id">
                        <span className="history-match-id-number">#{id}</span>
                        <div className="copy-button">
                            <CopyToClipboard text={id} onCopy={this.onClickCopyMessage}>
                                <span className="icon">
                                    <SpriteIcon name="#svg-copy-icon" className="icon"/>
                                </span>
                            </CopyToClipboard>
                        </div>
                        {this.renderBuyoutStatus()}
                    </div>
                </div>
                <div className="history-match-teams">
                    {gameImage(filter, 'history-match-game-logo')}
                    <span className="history-match-teams-names">{gamer_1.nick} vs {gamer_2.nick}</span>
                </div>
                {this.isNestedBet() && <span className="history-match-details">
                    {extended_gamer}
                </span>}
                <div className="history-match-team">
                    {flagImage(flag, 'history-match-team-country-flag')}
                    {raceImage(race, 'history-match-team-country-flag')}
                    <span className="history-match-team-name">{this.isNestedBet() ? gamer_nick : extended_gamer}</span>
                </div>
            </div>
            <div className="table-content-inner-row-mobile">
                <div className="cell">
                    <span className="cell-title">{I18n.t('bet')}:</span>
                    {bet_artifacts
                        ? <div className="history-match-artifacts">
                            {bet_artifacts.map((a, i) => <>
                                {Array.from({length: a.total_inventory_items
                                    ? a.total_inventory_items
                                    : 1}, (val, index) => <span
                                    key={`artifact_${index}`}
                                    className="history-match-artifact">
                                    <SimpleArtifactImage
                                        isClickable
                                        imgAttributes={{className: 'image-inventory cursor-pointer'}}
                                        artifact={a}
                                        key={index + i}
                                    />
                                </span>)}
                            </>)}
                        </div>
                        : <span className="history-match-sum">{betMoney(this.props)}</span>}
                </div>
                <div className="cell">
                    {this.renderSportsBook(coef, activeClass)}
                </div>
                <div className="cell">
                    <span className="cell-title">{I18n.t(this.props.bought_out ? 'buyout.button' : 'won')}:</span>
                    {win_artifacts
                        ? <div className="history-match-artifacts">
                            {win_artifacts.map((a, i) => <>
                                {Array.from({length: a.total_inventory_items
                                    ? a.total_inventory_items
                                    : 1}, (val, index) => <span
                                    key={`win_artifact_${index}`}
                                    className="history-match-artifact">
                                    <SimpleArtifactImage
                                        isClickable
                                        imgAttributes={{className: 'image-inventory cursor-pointer'}}
                                        artifact={a}
                                        key={index + i}
                                    />
                                </span>)}
                            </>)}
                        </div>
                        : <span className="history-sum">{this.props.won !== null && this.props.won ||
                        this.props.won === null && !this.props.cancelled
                            ? winValue
                            : '-'}</span>}
                </div>
            </div>
            <div className="history-right-buttons">
                {cancelled ? <SmartTooltipReason {...this.props}/> : this.renderActionsButtons()}
            </div>
        </div>;
    }

    render(): React.ReactNode {
        if (!isDesktop()) {
            return this.renderMobile();
        }

        const {
            id, filter, flag, extended_gamer, playmoney, race,
            coef, amount, cancelled, bet_artifacts, win_artifacts,
            finished, sportsbook, win
        } = this.props;
        // eslint-disable-next-line no-unsafe-optional-chaining
        const {gamer_1, gamer_2} = this.props.bet?.root_line || this.props?.bet || this.props;
        const gamer_nick = this.props.on === GAMER_1 ? gamer_1.nick : gamer_2.nick;
        const activeClass = this.props.isOpened ? ' active' : '';
        const winValue = betMoney({amount: win && win > 0 ? win : amount * coef, playmoney});

        return <div className={`table-content-row${this.rowStatusBet()}`}>
            <div className="table-content-cell">
                <div className="history-match">
                    <div className="history-match-date">
                        <span>{date(this.props.date)}</span>
                        <span>{time(this.props.date)}</span>
                    </div>
                    <div className="history-match-team">
                        {gameImage(filter, 'history-match-game-logo')}
                        <span className="history-match-team-name">
                            {gamer_1.nick} vs {gamer_2.nick} {finished && this.props.bet.game_name_score}
                        </span>
                    </div>
                    {this.isNestedBet() && <span className="history-match-details">
                        {extended_gamer}
                    </span>}
                    {!this.isNestedBet() && <div className="history-match-teams">
                        {flagImage(flag, 'history-match-team-country-flag')}
                        {raceImage(race, 'history-match-team-country-flag')}
                        <span className="history-match-teams-names">
                            {this.isNestedBet() ? gamer_nick : extended_gamer}
                        </span>
                    </div>}
                </div>
            </div>
            <div className="table-content-cell">
                {bet_artifacts
                    ? <div className="history-match-artifacts">
                        {bet_artifacts.map((a, i) => <>
                            {Array.from({length: a.total_inventory_items
                                ? a.total_inventory_items
                                : 1}, (val, index) => <span
                                key={`artifact_item_${index}`}
                                className="history-match-artifact">
                                <SimpleArtifactImage
                                    isClickable
                                    imgAttributes={{className: 'image-inventory cursor-pointer'}}
                                    artifact={a}
                                    key={index + i}
                                />
                            </span>)}
                        </>)}
                    </div>
                    : <span className="history-match-sum">{betMoney(this.props)}</span>}
            </div>
            <div className="table-content-cell">
                {this.renderSportsBook(coef, activeClass)}
            </div>
            <div className="table-content-cell">
                {win_artifacts
                    ? <div className="history-match-artifacts">
                        {win_artifacts.map((a, i) => <>
                            {Array.from({length: a.total_inventory_items
                                ? a.total_inventory_items
                                : 1}, (val, index) => <span
                                key={`win_artifact_item_${index}`}
                                className="history-match-artifact">
                                <SimpleArtifactImage
                                    isClickable
                                    imgAttributes={{className: 'image-inventory cursor-pointer'}}
                                    artifact={a}
                                    key={index + i}
                                />
                            </span>)}
                        </>)}
                    </div>
                    : <span className="history-sum">{this.props.won !== null && this.props.won ||
                    this.props.won === null && !this.props.cancelled
                        ? winValue
                        : '-'}</span>}
            </div>
            <div className="table-content-cell">
                <div className="history-match-id">
                    <span className="history-match-id-number">{id}</span>
                    <div className="copy-button">
                        <CopyToClipboard text={id} onCopy={this.onClickCopyMessage}>
                            <span className="icon">
                                <SpriteIcon name="#svg-copy-icon" className="svg-default"/>
                            </span>
                        </CopyToClipboard>
                    </div>
                    {this.renderBuyoutStatus()}
                </div>
                {sportsbook
                    ? <></>
                    : <div className="history-right-buttons">
                        {cancelled ? <SmartTooltipReason {...this.props}/> : this.renderActionsButtons()}
                    </div>}
            </div>
        </div>;
    }
}
